import React, { useState, useEffect } from 'react';
import authService from '../services/authService';

/**
 * Logout Test Component
 * Comprehensive testing of logout functionality
 */
const LogoutTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [authStatus, setAuthStatus] = useState({});

  useEffect(() => {
    updateAuthStatus();
  }, []);

  const updateAuthStatus = () => {
    const token = authService.getToken();
    const user = authService.getStoredUser();
    const status = {
      isAuthenticated: authService.isAuthenticated(),
      hasToken: !!token,
      hasStoredUser: !!user,
      token: token ? `${token.substring(0, 20)}...` : 'None',
      userEmail: user?.email || 'None',
      userRole: user?.role || 'None'
    };
    setAuthStatus(status);
  };

  const tests = [
    {
      name: 'Login Test (Setup)',
      test: async () => {
        console.log('[LogoutTest] Testing login first');
        const result = await authService.login('manager@company.com', 'admin123');
        updateAuthStatus();
        
        return {
          success: true,
          hasToken: !!result.token,
          hasUser: !!result.user,
          userEmail: result.user?.email,
          tokenLength: result.token?.length
        };
      }
    },
    {
      name: 'Pre-Logout Auth Check',
      test: async () => {
        const preLogout = {
          isAuthenticated: authService.isAuthenticated(),
          hasToken: !!authService.getToken(),
          hasStoredUser: !!authService.getStoredUser(),
          localStorageToken: !!localStorage.getItem('authToken'),
          sessionStorageToken: !!sessionStorage.getItem('authToken'),
          localStorageUser: !!localStorage.getItem('user')
        };
        
        return {
          success: true,
          preLogout,
          allAuthDataPresent: preLogout.isAuthenticated && preLogout.hasToken && preLogout.hasStoredUser
        };
      }
    },
    {
      name: 'Logout Data Clear Test',
      test: async () => {
        console.log('[LogoutTest] Testing logout data clearing');
        
        // Store pre-logout state
        const preLogout = {
          wasAuthenticated: authService.isAuthenticated(),
          hadToken: !!authService.getToken(),
          hadUser: !!authService.getStoredUser()
        };
        
        // Clear auth data manually (simulating logout without redirect)
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        sessionStorage.removeItem('authToken');
        
        // Check post-logout state
        const postLogout = {
          isAuthenticated: authService.isAuthenticated(),
          hasToken: !!authService.getToken(),
          hasStoredUser: !!authService.getStoredUser(),
          localStorageToken: !!localStorage.getItem('authToken'),
          sessionStorageToken: !!sessionStorage.getItem('authToken'),
          localStorageUser: !!localStorage.getItem('user')
        };
        
        updateAuthStatus();
        
        return {
          success: true,
          preLogout,
          postLogout,
          dataClearedSuccessfully: !postLogout.isAuthenticated && 
                                   !postLogout.hasToken && 
                                   !postLogout.hasStoredUser &&
                                   !postLogout.localStorageToken &&
                                   !postLogout.sessionStorageToken &&
                                   !postLogout.localStorageUser
        };
      }
    },
    {
      name: 'Authentication Status After Logout',
      test: async () => {
        const authCheck = {
          isAuthenticated: authService.isAuthenticated(),
          hasToken: !!authService.getToken(),
          hasStoredUser: !!authService.getStoredUser(),
          tokenFromLocalStorage: localStorage.getItem('authToken'),
          tokenFromSessionStorage: sessionStorage.getItem('authToken'),
          userFromLocalStorage: localStorage.getItem('user')
        };
        
        return {
          success: true,
          authCheck,
          completelyLoggedOut: !authCheck.isAuthenticated && 
                              !authCheck.hasToken && 
                              !authCheck.hasStoredUser &&
                              !authCheck.tokenFromLocalStorage &&
                              !authCheck.tokenFromSessionStorage &&
                              !authCheck.userFromLocalStorage
        };
      }
    },
    {
      name: 'API Call After Logout (Should Fail)',
      test: async () => {
        console.log('[LogoutTest] Testing API call after logout');
        try {
          await authService.getCurrentUser();
          // If this succeeds, something is wrong
          return {
            success: false,
            error: 'API call should have failed after logout',
            unexpectedSuccess: true
          };
        } catch (error) {
          // This is expected
          return {
            success: true,
            expectedFailure: true,
            errorMessage: error.message,
            isUnauthorized: error.message.includes('401') || error.message.includes('Unauthorized')
          };
        }
      }
    }
  ];

  const runLogoutTest = async () => {
    setIsRunning(true);
    setTestResults([]);
    
    const results = [];
    
    for (const test of tests) {
      console.log(`[LogoutTest] Running: ${test.name}`);
      const startTime = Date.now();
      
      try {
        const result = await test.test();
        const duration = Date.now() - startTime;
        
        results.push({
          name: test.name,
          success: true,
          result,
          duration,
          error: null
        });
      } catch (error) {
        const duration = Date.now() - startTime;
        
        results.push({
          name: test.name,
          success: false,
          result: null,
          duration,
          error: error.message
        });
      }
      
      setTestResults([...results]);
      
      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setIsRunning(false);
  };

  const clearAllAuth = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('authToken');
    updateAuthStatus();
    setTestResults([]);
  };

  const testActualLogout = () => {
    console.log('[LogoutTest] Testing actual logout function');
    // This will trigger the real logout with redirect
    authService.logout();
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Logout Functionality Test
        </h2>
        <p className="text-gray-600">
          Comprehensive testing of logout functionality including data clearing and authentication status.
        </p>
      </div>

      {/* Current Auth Status */}
      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="font-semibold text-gray-800 mb-2">Current Authentication Status:</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><strong>Is Authenticated:</strong> {authStatus.isAuthenticated ? '✅ Yes' : '❌ No'}</div>
          <div><strong>Has Token:</strong> {authStatus.hasToken ? '✅ Yes' : '❌ No'}</div>
          <div><strong>Has User:</strong> {authStatus.hasStoredUser ? '✅ Yes' : '❌ No'}</div>
          <div><strong>Token:</strong> {authStatus.token}</div>
          <div><strong>User Email:</strong> {authStatus.userEmail}</div>
          <div><strong>User Role:</strong> {authStatus.userRole}</div>
        </div>
      </div>

      {/* Control Buttons */}
      <div className="mb-6 flex gap-4 flex-wrap">
        <button
          onClick={runLogoutTest}
          disabled={isRunning}
          className={`px-6 py-3 rounded-lg font-medium ${
            isRunning 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {isRunning ? 'Running Tests...' : 'Run Logout Tests'}
        </button>
        
        <button
          onClick={testActualLogout}
          disabled={isRunning}
          className="px-6 py-3 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white"
        >
          Test Actual Logout (With Redirect)
        </button>
        
        <button
          onClick={clearAllAuth}
          disabled={isRunning}
          className="px-6 py-3 rounded-lg font-medium bg-yellow-600 hover:bg-yellow-700 text-white"
        >
          Clear Auth Data
        </button>
        
        <button
          onClick={updateAuthStatus}
          disabled={isRunning}
          className="px-6 py-3 rounded-lg font-medium bg-gray-600 hover:bg-gray-700 text-white"
        >
          Refresh Status
        </button>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Test Results:</h3>
          
          {testResults.map((test, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border ${
                test.success 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className={`font-medium ${
                  test.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {test.success ? '✅' : '❌'} {test.name}
                </h4>
                <span className="text-sm text-gray-500">
                  {test.duration}ms
                </span>
              </div>
              
              {test.success && test.result && (
                <div className="text-sm">
                  <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                    {JSON.stringify(test.result, null, 2)}
                  </pre>
                </div>
              )}
              
              {!test.success && (
                <div className="text-sm text-red-700">
                  <strong>Error:</strong> {test.error}
                </div>
              )}
            </div>
          ))}
          
          {/* Summary */}
          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="font-semibold text-gray-800 mb-2">Test Summary:</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-green-600">✅ Passed: </span>
                {testResults.filter(r => r.success).length}
              </div>
              <div>
                <span className="text-red-600">❌ Failed: </span>
                {testResults.filter(r => !r.success).length}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogoutTest;